import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Bell,
    CheckCheck,
    Trash2,
    X,
    FileText,
    GraduationCap,
    ListChecks,
    CheckSquare,
    Clock,
    AlertTriangle,
    MessageSquare,
    ClipboardList,
    Loader2,
    ChevronLeft,
    Filter
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

const EmployeeNotificationsPage = () => {
    const navigate = useNavigate();
    const { token } = useParams();
    const location = useLocation();
    const {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications
    } = useNotifications();

    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'tasks', 'messages', 'surveys'
    const pageSize = 10;

    // Determine base path for navigation (preboarding vs regular employee)
    const isPreboarding = location.pathname.includes('/preboard/');
    const basePath = isPreboarding ? `/preboard/${token}/dashboard` : '/employee';

    useEffect(() => {
        fetchNotifications();
    }, []);

    useEffect(() => {
        // Filter and paginate notifications
        let filtered = [...notifications];

        if (filter === 'unread') {
            filtered = filtered.filter(n => !n.is_read);
        } else if (filter === 'tasks') {
            filtered = filtered.filter(n =>
                n.notification_type?.includes('task') ||
                (!n.notification_type?.includes('message') && !n.notification_type?.includes('survey'))
            );
        } else if (filter === 'messages') {
            filtered = filtered.filter(n => n.notification_type === 'message_received');
        } else if (filter === 'surveys') {
            filtered = filtered.filter(n => n.notification_type === 'survey_assigned');
        }

        setDisplayedNotifications(filtered.slice(0, page * pageSize));
    }, [notifications, page, filter]);

    const hasMore = displayedNotifications.length < notifications.filter(n => {
        if (filter === 'unread') return !n.is_read;
        if (filter === 'tasks') return n.notification_type?.includes('task') || (!n.notification_type?.includes('message') && !n.notification_type?.includes('survey'));
        if (filter === 'messages') return n.notification_type === 'message_received';
        if (filter === 'surveys') return n.notification_type === 'survey_assigned';
        return true;
    }).length;

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    const getTaskIcon = (taskType, notificationType) => {
        if (notificationType === 'message_received') {
            return <MessageSquare size={20} className="text-primary" />;
        }
        if (notificationType === 'survey_assigned') {
            return <ClipboardList size={20} className="text-purple-500" />;
        }

        switch (taskType) {
            case 'quiz':
                return <FileText size={20} className="text-purple-500" />;
            case 'training':
                return <GraduationCap size={20} className="text-blue-500" />;
            case 'checklist':
                return <ListChecks size={20} className="text-green-500" />;
            case 'message':
                return <MessageSquare size={20} className="text-primary" />;
            default:
                return <CheckSquare size={20} className="text-primary" />;
        }
    };

    const getTaskBg = (taskType, notificationType) => {
        if (notificationType === 'message_received') {
            return 'bg-primary/10';
        }
        if (notificationType === 'survey_assigned') {
            return 'bg-purple-50';
        }

        switch (taskType) {
            case 'quiz':
                return 'bg-purple-50';
            case 'training':
                return 'bg-blue-50';
            case 'checklist':
                return 'bg-green-50';
            case 'message':
                return 'bg-primary/10';
            default:
                return 'bg-primary/10';
        }
    };

    const getNotificationTypeIcon = (type) => {
        switch (type) {
            case 'task_due_soon':
                return <Clock size={20} className="text-orange-500" />;
            case 'task_overdue':
                return <AlertTriangle size={20} className="text-red-500" />;
            case 'message_received':
                return <MessageSquare size={20} className="text-primary" />;
            case 'survey_assigned':
                return <ClipboardList size={20} className="text-purple-500" />;
            default:
                return null;
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return created.toLocaleDateString();
    };

    const formatDueDate = (dueDate) => {
        if (!dueDate) return null;

        const date = new Date(dueDate);
        const now = new Date();
        const diffTime = date - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { text: 'Overdue', color: 'text-red-600 bg-red-50' };
        if (diffDays === 0) return { text: 'Due Today', color: 'text-orange-600 bg-orange-50' };
        if (diffDays === 1) return { text: 'Due Tomorrow', color: 'text-orange-500 bg-orange-50' };
        if (diffDays <= 3) return { text: `Due in ${diffDays} days`, color: 'text-yellow-600 bg-yellow-50' };
        return null;
    };

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        switch (notification.notification_type) {
            case 'message_received':
                navigate(`${basePath}/messages`);
                break;
            case 'survey_assigned':
                navigate(`${basePath}/surveys`);
                break;
            default:
                navigate(`${basePath}/tasks`);
                break;
        }
    };

    const getTypeLabel = (notification) => {
        if (notification.notification_type === 'message_received') return 'message';
        if (notification.notification_type === 'survey_assigned') return 'survey';
        return notification.task_type || 'task';
    };

    const filterOptions = [
        { value: 'all', label: 'All' },
        { value: 'unread', label: 'Unread' },
        { value: 'tasks', label: 'Tasks' },
        { value: 'messages', label: 'Messages' },
        { value: 'surveys', label: 'Surveys' }
    ];

    return (
        <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ChevronLeft size={20} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Bell size={24} className="text-primary" />
                            Notifications
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                        <button
                            onClick={markAllAsRead}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <CheckCheck size={16} />
                            Mark all read
                        </button>
                    )}
                    {notifications.length > 0 && (
                        <button
                            onClick={dismissAllNotifications}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                            <Trash2 size={16} />
                            Clear all
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                <Filter size={16} className="text-gray-400 flex-shrink-0" />
                {filterOptions.map(option => (
                    <button
                        key={option.value}
                        onClick={() => {
                            setFilter(option.value);
                            setPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filter === option.value
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {/* Notifications List */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center">
                        <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
                        <p className="text-gray-500">Loading notifications...</p>
                    </div>
                ) : displayedNotifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <Bell size={48} className="text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No notifications</p>
                        <p className="text-gray-400 text-sm mt-1">
                            {filter !== 'all' ? 'Try changing the filter' : "You're all caught up!"}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="divide-y divide-gray-100">
                            {displayedNotifications.map((notification) => {
                                const dueInfo = formatDueDate(notification.due_date);
                                const isMessageNotification = notification.notification_type === 'message_received';
                                const isSurveyNotification = notification.notification_type === 'survey_assigned';

                                return (
                                    <div
                                        key={notification.id}
                                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors group ${!notification.is_read ? 'bg-blue-50/50' : ''
                                            }`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${getTaskBg(notification.task_type, notification.notification_type)}`}>
                                                {getNotificationTypeIcon(notification.notification_type) || getTaskIcon(notification.task_type, notification.notification_type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className={`text-base ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                        {notification.title}
                                                    </p>
                                                    {!notification.is_read && (
                                                        <span className="w-2.5 h-2.5 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                    <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium capitalize">
                                                        {getTypeLabel(notification)}
                                                    </span>
                                                    {dueInfo && !isMessageNotification && !isSurveyNotification && (
                                                        <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${dueInfo.color}`}>
                                                            {dueInfo.text}
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {formatTimeAgo(notification.created_at)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    dismissNotification(notification.id);
                                                }}
                                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                title="Dismiss"
                                            >
                                                <X size={16} className="text-gray-400" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Load More */}
                        {hasMore && (
                            <div className="p-4 border-t border-gray-100">
                                <button
                                    onClick={loadMore}
                                    className="w-full py-2.5 text-sm text-primary hover:text-primary-hover font-medium hover:bg-primary/5 rounded-lg transition-colors"
                                >
                                    Load more notifications
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EmployeeNotificationsPage;
